import { ChangeEvent, useContext, useState } from 'react';
import { Button, FormRadio, Modal, ModalDescription, ModalFooter, ModalHeader, ModalTitle, SwitchThemeContext, TextField } from '@tryhackme/thm-ui-components';
import { useGetUserQuery } from 'src/common/slices';
import { useUpdateGeneralInformationMutation } from 'src/features/manage-account/account-details/sections/general-information/general-information.slice';
import { GeneralInformationFormData } from 'src/features/manage-account/account-details/sections/general-information/general-information.types';
import certificatePlaceHolderPNG from 'src/images/svgs/certifications/certificate-placeholder.svg';
import { ThemeColorMode } from 'src/common/enums';
import { StyledCertificatePlaceholder, StyledCloseButton, StyledLeftContainer, StyledOptionBox, StyledOptionTextContainer, StyledRightContainer, StyledLighterText, StyledMainContainer } from './certificate-full-name-modal.styles';
import { CertificateFullNameModalProps } from './certificate-full-name-modal.types';
export const CertificateFullNameModal = ({
  handleCertificate,
  isCertificateLoading,
  isModalOpen,
  setIsModalOpen
}: CertificateFullNameModalProps) => {
  const {
    theme
  } = useContext(SwitchThemeContext);
  const isDarkMode = theme === ThemeColorMode.DARK;
  const {
    data: userData
  } = useGetUserQuery();
  const user = userData?.data?.user;
  const [radioValue, setRadioValue] = useState<'fullName' | 'username'>('fullName');
  const [fullName, setFullName] = useState<string>('');
  const [updateGeneralInformation, {
    isLoading: isUserUpdateLoading
  }] = useUpdateGeneralInformationMutation();
  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setRadioValue((event.target.value as 'fullName' | 'username'));
    }
  };
  const handleFullNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFullName(event.target.value);
  };
  const handleUserUpdate = async (data: GeneralInformationFormData) => {
    await updateGeneralInformation({
      ...data,
      email: user?.email,
      username: user?.username
    });
    await handleCertificate();
  };
  const handleModalSubmit = async () => {
    // eslint-disable-next-line unicorn/prefer-ternary
    if (radioValue === 'fullName') {
      await handleUserUpdate({
        fullName
      });
    } else {
      await handleCertificate();
    }
    setIsModalOpen(false);
  };
  return <Modal onOpenChange={setIsModalOpen} open={isModalOpen} data-sentry-element="Modal" data-sentry-component="CertificateFullNameModal" data-sentry-source-file="certificate-full-name-modal.tsx">
      <ModalHeader isSmall data-sentry-element="ModalHeader" data-sentry-source-file="certificate-full-name-modal.tsx">
        <ModalTitle data-sentry-element="ModalTitle" data-sentry-source-file="certificate-full-name-modal.tsx">Add your full name to your certificate of completion?</ModalTitle>
      </ModalHeader>
      <main>
        <ModalDescription data-sentry-element="ModalDescription" data-sentry-source-file="certificate-full-name-modal.tsx">
          Please choose if you would like to generate your certificate with your full name or username.
        </ModalDescription>
        <StyledMainContainer data-sentry-element="StyledMainContainer" data-sentry-source-file="certificate-full-name-modal.tsx">
          <StyledLeftContainer data-sentry-element="StyledLeftContainer" data-sentry-source-file="certificate-full-name-modal.tsx">
            <StyledOptionBox isSelected={radioValue === 'fullName'} onClick={() => setRadioValue('fullName')} data-sentry-element="StyledOptionBox" data-sentry-source-file="certificate-full-name-modal.tsx">
              <FormRadio label="" value="fullName" checked={radioValue === 'fullName'} data-sentry-element="FormRadio" data-sentry-source-file="certificate-full-name-modal.tsx" />
              <StyledOptionTextContainer data-sentry-element="StyledOptionTextContainer" data-sentry-source-file="certificate-full-name-modal.tsx">
                <p>Add your full name to your certificate</p>
                <TextField value={fullName} variant="input" onChange={handleFullNameChange} placeholder="Enter your full name" label="Full name" disabled={radioValue === 'username'} width="100%" isRequired data-sentry-element="TextField" data-sentry-source-file="certificate-full-name-modal.tsx" />
              </StyledOptionTextContainer>
            </StyledOptionBox>
            <StyledOptionBox isSelected={radioValue === 'username'} onClick={() => setRadioValue('username')} data-sentry-element="StyledOptionBox" data-sentry-source-file="certificate-full-name-modal.tsx">
              <FormRadio value="username" checked={radioValue === 'username'} onChange={handleRadioChange} label="" data-sentry-element="FormRadio" data-sentry-source-file="certificate-full-name-modal.tsx" />
              <div>
                <p>Add your username to your certificate:</p>
                <StyledLighterText isDarkMode={isDarkMode} data-sentry-element="StyledLighterText" data-sentry-source-file="certificate-full-name-modal.tsx">Username: {user?.username ?? ''}</StyledLighterText>
              </div>
            </StyledOptionBox>
          </StyledLeftContainer>
          <StyledRightContainer data-sentry-element="StyledRightContainer" data-sentry-source-file="certificate-full-name-modal.tsx">
            <StyledLighterText isDarkMode={isDarkMode} data-sentry-element="StyledLighterText" data-sentry-source-file="certificate-full-name-modal.tsx">Certificate of completion:</StyledLighterText>
            <StyledCertificatePlaceholder isDarkMode={isDarkMode} src={certificatePlaceHolderPNG} alt="Certificate of completion" data-sentry-element="StyledCertificatePlaceholder" data-sentry-source-file="certificate-full-name-modal.tsx" />
          </StyledRightContainer>
        </StyledMainContainer>
      </main>
      <ModalFooter isSmall data-sentry-element="ModalFooter" data-sentry-source-file="certificate-full-name-modal.tsx">
        <StyledCloseButton onClick={() => setIsModalOpen(false)} color="secondary" data-sentry-element="StyledCloseButton" data-sentry-source-file="certificate-full-name-modal.tsx">
          Cancel
        </StyledCloseButton>
        <Button data-testid="certificate-modal-submit" disabled={radioValue === 'fullName' && !fullName} variant="solid" color="primary" isLoading={isUserUpdateLoading || isCertificateLoading}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      onClick={handleModalSubmit} data-sentry-element="Button" data-sentry-source-file="certificate-full-name-modal.tsx">
          Generate certificate
        </Button>
      </ModalFooter>
    </Modal>;
};